import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/lib";

const badgeVariants = cva(
  "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary/80",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
        outline: "text-foreground",
        orange: "border-orange-500 bg-orange-500 text-white hover:bg-orange-500/80",
        /** Active / enabled pill: pale mint surface, seafoam border, deep green text */
        statusActive:
          "border border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-700/55 dark:bg-emerald-950/45 dark:text-emerald-100",
        /** Status theme: success (e.g. completed, delivered) */
        statusSuccess:
          "border-emerald-500/50 bg-emerald-500/15 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
        /** Status theme: warning (e.g. open, pending, ordered) */
        statusWarning:
          "border-amber-500/50 bg-amber-500/15 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200",
        /** Status theme: info (e.g. scheduled) */
        statusInfo:
          "border-blue-500/50 bg-blue-500/15 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300",
        /** Status theme: neutral (e.g. inactive, closed) */
        statusNeutral: "border-muted bg-muted/80 text-muted-foreground",
        /** Ticket OPEN / NEW — violet */
        statusViolet:
          "border-violet-500/50 bg-violet-500/15 text-violet-900 dark:border-violet-500/40 dark:bg-violet-500/20 dark:text-violet-200",
        /** Ticket IN_PROGRESS — teal (active work; distinct from MEDIUM priority blue) */
        statusTeal:
          "border-teal-500/50 bg-teal-500/15 text-teal-900 dark:border-teal-500/40 dark:bg-teal-500/20 dark:text-teal-200",
        /** Ticket SCHEDULED — indigo (planned slot) */
        statusIndigo:
          "border-indigo-500/50 bg-indigo-500/15 text-indigo-900 dark:border-indigo-500/40 dark:bg-indigo-500/20 dark:text-indigo-200",
        /** Ticket WAITING_FOR_SPARE_PARTS — orange (blocked on supply) */
        statusOrange:
          "border-orange-500/50 bg-orange-500/15 text-orange-950 dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-200",
        /** Ticket QUESTION — cyan (follow-up / clarification) */
        statusCyan:
          "border-cyan-500/50 bg-cyan-500/15 text-cyan-950 dark:border-cyan-500/40 dark:bg-cyan-500/20 dark:text-cyan-200",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, ...props }, ref) => {
    return <div ref={ref} className={cn(badgeVariants({ variant }), className)} {...props} />;
  }
);
Badge.displayName = "Badge";

export { Badge, badgeVariants }; // eslint-disable-line react-refresh/only-export-components
